import { useEffect } from "react";
import { OrbitControls } from "@react-three/drei";
import { useThree } from "@react-three/fiber";
import type { Vec3 } from "@solblox/shared";

import { useStore } from "../store";

interface SpectatorCameraProps {
  /** Where the orbit pivot sits. Same world space the lobby scene uses. */
  target: Vec3;
  /** Initial camera offset from the target. */
  offset?: [number, number, number];
}

/**
 * Free-flying spectator camera. Replaces the Ecctrl follow-cam + rigid body
 * when the local player joined with `asSpectator: true`. Mouse-drag orbits,
 * wheel zooms — drei's OrbitControls covers it. No physics, no broadcast.
 */
export function SpectatorCamera({
  target,
  offset = [0, 6, 16],
}: SpectatorCameraProps): JSX.Element {
  const camera = useThree((s) => s.camera);
  const aerial = useStore((s) => s.aerialView);

  useEffect(() => {
    camera.position.set(
      target.x + offset[0],
      target.y + offset[1],
      target.z + offset[2],
    );
    camera.lookAt(target.x, target.y, target.z);
    // Run once on mount — subsequent updates flow through OrbitControls.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Yield the camera to <AerialCamera> while the admin orbit is on — otherwise
  // OrbitControls (makeDefault) would fight it each frame. Lets a streamer film
  // the cinematic orbit from spectator mode (no avatar in the shot).
  if (aerial) return <></>;

  return (
    <OrbitControls
      makeDefault
      target={[target.x, target.y + 1.5, target.z]}
      enablePan
      enableDamping
      dampingFactor={0.1}
      minDistance={3}
      maxDistance={40}
      maxPolarAngle={Math.PI * 0.49}
    />
  );
}
